/**
 * BAIGTA and PERŽIŪRA (surfaces.md §1.2): the test's end, then the items she
 * missed or skipped, root first, as the server ordered them. Each shows the
 * question, her answer (or „praleista"), „Teisingas atsakymas", and the door
 * where the Move has a vetted explanation. Items she got right are not listed.
 *
 * `↑`/`↓` move between items, Enter opens a door (the beats, then RUNG 1 on
 * that item's own variant), Esc returns to the list, „Toliau" leaves. The
 * review offers and never forces; it is not a second test.
 */
import { useEffect, useRef, useState } from "react";

import { ulid } from "../lib/ulid";
import { Rich } from "../lib/math";
import { TruthView } from "./Item";
import { type ChildExplanation, type ChildReviewItem, childApi } from "./api";
import { answerText } from "./flow/practice";
import { Explain } from "./Explain";
import { Rung, type RungHandle, ladderLength } from "./Rung";
import { useChild, useKeys } from "./ctx";

export function Baigta({ onGo }: { onGo: () => void }) {
  useKeys({ Enter: onGo });
  return (
    <div data-phase="BAIGTA">
      <p className="prompt">Baigta.</p>
      <div className="actions">
        <button type="button" className="btn btn-solid" onClick={onGo} data-act="next">
          Toliau <span className="key-hint">Enter</span>
        </button>
      </div>
    </div>
  );
}

type Open = { index: number; opened: string; x: ChildExplanation; at: "BEATS" | "RUNG"; openedAt: number };

export function Review({ test, items, onDone }: { test: string; items: ChildReviewItem[]; onDone: () => void }) {
  const ctx = useChild();
  const [sel, setSel] = useState(0);
  const [open, setOpen] = useState<Open | null>(null);
  const [failure, setFailure] = useState("");
  const shownAt = useRef(performance.now());
  const rung = useRef<RungHandle | null>(null);

  useEffect(() => {
    ctx.setWhere({ test, state: "PERŽIŪRA", rung: 0, where: open ? "explanation" : "item", open: false });
    return () => ctx.setWhere(null);
  }, [ctx, test, open]);

  const close = (by: "close" | "next_item") => {
    if (!open) return;
    rung.current?.leave();
    const ms = Math.round(performance.now() - open.openedAt);
    ctx.emit("explanation.closed", { opened: open.opened, by, open_ms: ms, visible_ms: ms, last_beat: open.x.beats.length - 1, max_rung: open.at === "RUNG" ? 1 : 0 });
    setOpen(null);
  };

  const door = async (i: number) => {
    const r = items[i];
    if (!r?.door) return;
    const opened = ulid();
    try {
      await ctx.queue.flush();
      const x = await childApi.openExplanation({ id: opened, item: r.item.handle, explanation: r.door.explanation, door: "post_test", test });
      setOpen({ index: i, opened, x, at: "BEATS", openedAt: performance.now() });
    } catch (e) {
      if (!ctx.lost(e)) setFailure("Nepavyko atidaryti.");
    }
  };

  const leave = () => {
    close("next_item");
    ctx.emit("test.review_closed", { test, by: "done", open_ms: Math.round(performance.now() - shownAt.current) });
    void ctx.queue.flush();
    onDone();
  };

  useKeys(
    {
      ArrowDown: () => setSel((s) => Math.min(items.length - 1, s + 1)),
      ArrowUp: () => setSel((s) => Math.max(0, s - 1)),
      Enter: () => void door(sel),
      Escape: () => undefined,
    },
    !open,
  );
  useKeys({ Escape: () => close("close"), Enter: () => close("close") }, open?.at === "RUNG");

  if (open) {
    const r = items[open.index];
    return (
      <div data-phase={open.at === "BEATS" ? "BEATS" : "RUNG"} data-mode="review">
        <div className="prompt">
          <Rich text={r.item.prompt} />
        </div>
        {open.at === "BEATS" ? (
          <Explain explanation={open.x} opened={open.opened} onDone={() => setOpen({ ...open, at: "RUNG" })} onEscape={() => close("close")} />
        ) : (
          <>
            <Rung
              drawing={r.item.drawing}
              rung={1}
              of={ladderLength(r.item.drawing)}
              opened={open.opened}
              item={r.item.handle}
              trigger="door"
              picked={r.answer ? answerText(r.answer) : undefined}
              bind={(h) => {
                rung.current = h;
              }}
            />
            <div className="actions">
              <button type="button" className="btn" onClick={() => close("close")} data-act="back">
                Grįžti į sąrašą <span className="key-hint">Esc</span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div data-phase="PERŽIŪRA">
      <p className="lead">Čia klausimai, kuriuos verta pažiūrėti dar kartą.</p>
      <div className="review-list">
        {items.map((r, i) => {
          const labels: Record<string, string> = {};
          for (const o of r.item.options ?? []) labels[o.id] = o.label;
          return (
            <div key={r.item.handle} className={`review-item ${i === sel ? "sel" : ""}`} data-review={i} onClick={() => setSel(i)}>
              <div className="prompt" style={{ fontSize: 24 }}>
                <Rich text={r.item.prompt} />
              </div>
              <div className="review-row">
                <span>
                  Tavo atsakymas: <b>{r.skipped || !r.answer ? "praleista" : <Rich text={answerText(r.answer, labels)} />}</b>
                </span>
                {r.truth && (
                  <span>
                    Teisingas atsakymas: <b>
                      <TruthView truth={r.truth} />
                    </b>
                  </span>
                )}
              </div>
              {r.door && (
                <div style={{ marginTop: 12 }}>
                  <button type="button" className="btn btn-small" onClick={() => void door(i)} data-act="door">
                    Noriu suprasti
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {failure && <p className="note">{failure}</p>}
      <div className="actions">
        <button type="button" className="btn btn-solid" onClick={leave} data-act="review-done">
          Toliau
        </button>
      </div>
    </div>
  );
}
